import Vue from "vue";
import VueRouter from "vue-router";
import LayoutMain from "../layouts/LayoutMain.vue";
import { applyPageMeta } from "@/utils/pageMeta";
import { resetPageContext } from "@/utils/pageContext";

Vue.use(VueRouter);

const UUID = "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}";

const bookingView = () =>
  import(/* webpackChunkName: "customer-booking-view" */ "@/views/CustomerBookingView.vue");
const finishBookingView = () =>
  import(/* webpackChunkName: "finish-booking-view" */ "@/views/FinishBookingView.vue");
const manageBookingView = () =>
  import(/* webpackChunkName: "manage-booking-view" */ "@/views/ManageBookingView.vue");
const photoQuoteView = () =>
  import(/* webpackChunkName: "photo-quote-view" */ "@/views/PhotoQuoteView.vue");

const BOOKING_META = {
  title: "Book a drop-off",
  description: "Book a local drop-off for your old tapes. It takes under a minute, with nothing to pay today.",
};
// Personal pages: noindex, generic title/description (brief "Share previews"),
// and the share bar shares the website instead of the page.
const PERSONAL_META = {
  title: "Your drop-off",
  description: "Pick or change your time, add your tapes, and see the details for your local drop-off.",
  noindex: true,
  personal: true,
};
const PHOTO_QUOTE_META = {
  title: "Get a quote from a photo",
  description: "Got old video tapes? Send us a photo. Free quote on WhatsApp or email. Converted locally on professional converters.",
};

const routes = [
  {
    path: "/",
    component: LayoutMain,
    children: [
      {
        path: "",
        name: "HomeView",
        component: bookingView,
        meta: BOOKING_META,
      },
      {
        path: "booking/:location",
        name: "CustomerBookingView",
        component: bookingView,
        props: true,
        meta: BOOKING_META,
      },
      {
        // Thank-you page. GTM's booking conversion fires on the route change
        // to this path — keep it a router.push, and tell Logan before
        // renaming /booking-confirmation
        path: "booking-confirmation",
        name: "CustomerBookingConfirmView",
        component: () =>
          import(
            /* webpackChunkName: "customer-booking-confirmation-view" */ "@/views/CustomerBookingConfirmView.vue"
          ),
        props: true,
        meta: PERSONAL_META,
      },
      {
        // Photo quote (replaces the old AI tape estimate)
        path: "tape-estimate",
        name: "TapeEstimateView",
        component: photoQuoteView,
        meta: PHOTO_QUOTE_META,
      },
      {
        path: "photo-quote",
        name: "PhotoQuoteView",
        component: photoQuoteView,
        meta: PHOTO_QUOTE_META,
      },
      {
        // HubSpot link_finish_booking: finish a booking, or rebook after a
        // missed drop-off. token = customers.uuid
        path: "r/:token/book",
        name: "FinishBookingView",
        component: finishBookingView,
        props: (route) => ({ token: route.params.token }),
        meta: PERSONAL_META,
      },
      {
        // HubSpot booking_change_link
        path: "r/:token/manage",
        name: "CustomerManageView",
        component: manageBookingView,
        props: (route) => ({ customerToken: route.params.token }),
        meta: PERSONAL_META,
      },
      {
        // A booking's own change link (older booking_change_link values)
        path: "manage/:token",
        name: "ManageBookingView",
        component: manageBookingView,
        props: (route) => ({ manageToken: route.params.token }),
        meta: PERSONAL_META,
      },
      {
        // Exit-popup quote link, sent by HubSpot once the contact is attached
        path: "quote/:code",
        name: "QuoteView",
        component: () =>
          import(
            /* webpackChunkName: "quote-view" */ "@/views/QuoteView.vue"
          ),
        props: true,
        meta: {
          title: "Get your quote",
          noindex: true,
        },
      },
      {
        path: "privacy-policy",
        name: "PrivacyPolicyView",
        component: () =>
          import(
            /* webpackChunkName: "privacy-policy-view" */ "@/views/PrivacyPolicyView.vue"
          ),
        props: true,
        meta: {
          title: "Privacy Policy",
        },
      },
      {
        path: "terms-of-service",
        name: "TermsOfServiceView",
        component: () =>
          import(
            /* webpackChunkName: "terms-of-service" */ "@/views/TermsOfServiceView.vue"
          ),
        props: true,
        meta: {
          title: "Terms of service",
        },
      },
      {
        // Older customer booking link, e.g. /f1404e66-6ab7-4c6a-b483-0e4222de7e23
        // (customers.booking_link) — now the same page as /r/<uuid>/book.
        // Only matches a UUID so location slugs below still work.
        path: `:customer(${UUID})`,
        name: "CustomerLinkBookingView",
        component: finishBookingView,
        props: (route) => ({ token: route.params.customer }),
        meta: PERSONAL_META,
      },
      {
        // Location slug straight off the root, e.g. /farnborough-gu14-8jj-whitehouse-close.
        // Must stay after the fixed pages above so they still match first.
        path: ":location",
        name: "LocationBookingView",
        component: bookingView,
        props: true,
        meta: BOOKING_META,
      },
      {
        path: "/:catchAll(.*)",
        name: "ErrorNotFoundView",
        component: () =>
          import(
            /* webpackChunkName: "not-found-view" */ "@/views/error/ErrorNotFoundView.vue"
          ),
        props: true,
        meta: { title: "Page not found", noindex: true },
      },
    ],
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
  scrollBehavior(to, from, savedPosition) {
    // If there's a saved position (e.g., navigating back), use it
    if (savedPosition) {
      return savedPosition;
    }
    // Otherwise, scroll to the top
    return { x: 0, y: 0 };
  },
});

// The header's WhatsApp context belongs to the page being left
router.beforeEach((to, from, next) => {
  resetPageContext();
  next();
});

router.afterEach((to) => {
  //BOC:[page title + robots]
  Vue.nextTick(() => applyPageMeta(to.meta));
  //EOC
});

export default router;
